import { createFileRoute, Link } from "@tanstack/react-router";
import { sanityClient, urlFor } from "@/lib/sanity";
import { useLocale, pickLocalized, tr, DEFAULT_LOCALE } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { TrendingCarousel } from "@/components/TrendingCarousel";
import { pageMeta, ogImageUrl, blogListingLd, breadcrumbLd, JsonLd, SITE_NAME } from "@/lib/seo";

const QUERY = `{
  "page": *[_type == "blogPage"][0]{ eyebrow, title, description, trendingSectionTitle, latestSectionTitle, seo },
  "trending": *[_type == "blogPost" && trending == true] | order(publishedAt desc)[0...2]{
    _id, title, slug, excerpt, coverImage, publishedAt, category
  },
  "latest": *[_type == "blogPost" && trending != true] | order(publishedAt desc){
    _id, title, slug, excerpt, coverImage, publishedAt, category
  }
}`;

export const Route = createFileRoute("/blog/")({
  component: BlogPage,
  loader: () => sanityClient.fetch<any>(QUERY),
  head: ({ loaderData }) =>
    pageMeta({
      title: pickLocalized(loaderData?.page?.seo?.metaTitle, DEFAULT_LOCALE) || "Blog — Matchamind",
      description: pickLocalized(loaderData?.page?.seo?.metaDescription, DEFAULT_LOCALE) || undefined,
      path: "/blog",
    }),
});

const copy = {
  eyebrow: { en: "THE MATCHAMIND BLOG", fr: "LE BLOG MATCHAMIND" },
  titlePart1: { en: "Slow reads for", fr: "Mots calmes pour" },
  titlePart2: { en: "busy minds.", fr: "esprits sans répit." },
  trending: { en: "Trending", fr: "Tendances" },
  latest: { en: "Latest articles", fr: "Derniers articles" },
} as const;

const CATEGORY_STYLES: Record<string, { dot: string; label: { en: string; fr: string } }> = {
  insights: {
    dot: "text-secondary-700",
    label: { en: "Insights", fr: "Réflexions" },
  },
  regulation: {
    dot: "text-primary-700",
    label: { en: "Regulation", fr: "Régulation" },
  },
  becoming: {
    dot: "text-accent-700",
    label: { en: "Becoming", fr: "Devenir" },
  },
  "self-talk": {
    dot: "text-highlight-600",
    label: { en: "Self-Talk", fr: "Discours intérieur" },
  },
};

function CategoryTag({ category, locale }: { category?: string; locale: "en" | "fr" }) {
  if (!category) return null;
  const key = category.toLowerCase();
  const style = CATEGORY_STYLES[key] ?? CATEGORY_STYLES.regulation;
  return (
    <span className="inline-flex h-5 items-center justify-center gap-1 rounded-[10px] bg-background px-2">
      <span
        className={cn(
          "font-sans text-[20px] font-black leading-[16px]",
          style.dot,
        )}
      >
        ·
      </span>
      <span className="font-sans text-[12px] font-light leading-[16px] text-primary-500">
        {tr(style.label, locale)}
      </span>
    </span>
  );
}

function formatDate(iso?: string, locale: "en" | "fr" = "en") {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(locale === "fr" ? "fr-FR" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

function PostCard({
  post,
  large = false,
  titleTag: TitleTag = "h3",
}: {
  post: any;
  large?: boolean;
  /** The mobile Trending carousel duplicates the desktop grid — pass "p" there so headings aren't repeated. */
  titleTag?: "h3" | "p";
}) {
  const locale = useLocale();
  const search = locale === "en" ? {} : { lang: "fr" as const };
  const slug = post.slug?.[locale]?.current || post.slug?.en?.current;

  return (
    <Link
      to="/blog/$slug"
      params={{ slug }}
      search={search}
      className={cn(
        "group flex flex-col overflow-hidden rounded-[24px] border border-[color:color-mix(in_oklab,var(--secondary-200)_60%,transparent)] bg-background-sand p-px",
        "shadow-[0_4px_20px_-2px_color-mix(in_oklab,var(--secondary-800)_5%,transparent)]",
        "transition-shadow hover:shadow-[0_8px_28px_-4px_color-mix(in_oklab,var(--secondary-800)_12%,transparent)]",
      )}
    >
      <div
        className={cn(
          "relative w-full overflow-hidden bg-surface-card-beige",
          large ? "aspect-[16/10]" : "aspect-[4/3]",
        )}
      >
        {post.coverImage ? (
          <img
            src={urlFor(post.coverImage)
              .width(large ? 1000 : 650)
              .height(large ? 625 : 488)
              .fit("crop")
              .url()}
            alt={pickLocalized(post.coverImage?.alt, locale) || pickLocalized(post.title, locale)}
            className="absolute inset-0 h-full w-full object-cover transition-transform group-hover:scale-[1.02]"
          />
        ) : null}
        <div className="absolute left-4 top-3">
          <CategoryTag category={post.category} locale={locale} />
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-2 px-6 pb-6 pt-[22px]">
        <TitleTag
          className={cn(
            "m-0 text-text-primary",
            large ? "text-[24px] leading-[32px]" : "text-[20px] leading-[26px]",
          )}
        >
          {pickLocalized(post.title, locale)}
        </TitleTag>
        {pickLocalized(post.excerpt, locale) && (
          <p className="font-sans text-[14px] font-light leading-[20px] text-text-secondary">
            {pickLocalized(post.excerpt, locale)}
          </p>
        )}
        <p className="mt-auto pt-2 font-sans text-[12px] leading-[16px] text-text-secondary">
          {formatDate(post.publishedAt, locale)}
        </p>
      </div>
    </Link>
  );
}

function BlogPage() {
  const locale = useLocale();
  const data = Route.useLoaderData();

  const eyebrow = pickLocalized(data?.page?.eyebrow, locale) || tr(copy.eyebrow, locale);
  const titleFromCms = pickLocalized(data?.page?.title, locale);

  const listedPosts = [...(data?.trending ?? []), ...(data?.latest ?? [])].filter(
    (p: any) => p?.slug?.en?.current,
  );

  return (
    <div className="mx-auto w-full max-w-[1040px] px-6 md:px-10">
      {listedPosts.length > 0 && (
        <JsonLd
          data={blogListingLd({
            name:
              pickLocalized(data?.page?.seo?.metaTitle, DEFAULT_LOCALE)?.trim() ||
              `${SITE_NAME} Blog`,
            description:
              pickLocalized(data?.page?.seo?.metaDescription, DEFAULT_LOCALE) || undefined,
            posts: listedPosts.map((p: any) => ({
              title: p.title,
              path: `/blog/${p.slug.en.current}`,
              description: pickLocalized(p.excerpt, DEFAULT_LOCALE) || undefined,
              image: ogImageUrl(p.coverImage),
              datePublished: p.publishedAt,
            })),
          })}
        />
      )}
      <JsonLd
        data={breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
        ])}
      />
      {/* Header */}
      <section className="flex flex-col items-start gap-6 py-16 md:py-[100px]">
        <div className="inline-flex items-center justify-center rounded-[43px] bg-secondary-300 px-2 py-1">
          <span className="font-sans text-[14px] leading-[14px] font-medium uppercase tracking-[1.4px] text-text-muted">
            {eyebrow}
          </span>
        </div>
        <h1 className="font-display text-[40px] leading-[44px] font-semibold tracking-[-0.5px]">
          {titleFromCms ? (
            <span className="text-text-primary">{titleFromCms}</span>
          ) : (
            <>
              <span className="text-text-primary">{tr(copy.titlePart1, locale)} </span>
              <span className="text-secondary-800">{tr(copy.titlePart2, locale)}</span>
            </>
          )}
        </h1>
        {pickLocalized(data?.page?.description, locale) && (
          <p className="max-w-2xl font-sans text-[18px] leading-[24px] text-text-secondary">
            {pickLocalized(data.page.description, locale)}
          </p>
        )}
      </section>

      {/* Trending */}
      {data?.trending?.length > 0 && (
        <section className="pb-16">
          <h2 className="mb-6 px-[10px] font-display text-[28px] leading-[36px] font-semibold tracking-[-0.112px] text-text-primary">
            {pickLocalized(data.page?.trendingSectionTitle, locale) || tr(copy.trending, locale)}
          </h2>
          <TrendingCarousel
            items={data.trending}
            renderItem={(p: any) => <PostCard post={p} large titleTag="p" />}
          />
          <div className="hidden gap-8 md:grid md:grid-cols-2">
            {data.trending.map((p: any) => (
              <PostCard key={p._id} post={p} large />
            ))}
          </div>
        </section>
      )}

      {/* Latest articles */}
      <section className="pb-24">
        <h2 className="mb-6 px-[10px] font-display text-[28px] leading-[36px] font-semibold tracking-[-0.112px] text-text-primary">
          {pickLocalized(data?.page?.latestSectionTitle, locale) || tr(copy.latest, locale)}
        </h2>
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {data?.latest?.map((p: any) => (
            <PostCard key={p._id} post={p} />
          ))}
        </div>
      </section>
    </div>
  );
}

